import { ChangeEvent, useEffect, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useClearEntityFilters, useEntities } from '@mb.io/core-components';
import { EntityTextFilter } from '@backstage/plugin-catalog-react';
import { debounce } from 'lodash';
import { useCatalogContext } from '../../context/CatalogContext';

class CustomEntityTextFilter extends EntityTextFilter {
  getFullTextFilters() {
    return {
      term: this.value,
      fields: [
        'metadata.name',
        'metadata.title',
        'metadata.description',
        'metadata.tags',
        'spec.profile.displayName',
      ],
    };
  }
}

const useStyles = makeStyles(theme => ({
  textField: {
    width: '100%',
    borderRadius: '2px',
    borderBottom:
      theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.70)'
        : '1px solid rgba(0, 0, 0, 0.60)',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.02)',
  },
  icon: {
    color:
      theme.palette.mode === 'dark'
        ? 'rgb(255, 255, 255, 0.54)'
        : 'rgba(0, 0, 0, 0.54)',
  },
  closeIcon: { cursor: 'pointer' },
}));

export type SearchbarType = {
  debounceInMilliseconds?: number;
};

export const Searchbar = ({
  debounceInMilliseconds = 700,
}: Readonly<SearchbarType>) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { updateFilters } = useEntities();
  const { searchTerm = '', setSearchTerm } = useCatalogContext();
  const { clearFilterState, resetFilters } = useClearEntityFilters();

  const debouncedUpdateFilters = useMemo(
    () =>
      debounce((value: string) => {
        updateFilters({
          text: new CustomEntityTextFilter(value),
        });
      }, debounceInMilliseconds),
    [debounceInMilliseconds, updateFilters],
  );

  useEffect(() => {
    if (clearFilterState.search) {
      setSearchTerm?.('');
      debouncedUpdateFilters('');
    }
  }, [clearFilterState.search, debouncedUpdateFilters, setSearchTerm]);

  const updateSearchTerm = (term: string) => {
    setSearchTerm?.(term);
    debouncedUpdateFilters(term);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    updateSearchTerm(term);
    resetFilters('search');
  };

  return (
    <TextField
      data-testid="catalog-search"
      placeholder="Search"
      value={searchTerm}
      onChange={handleSearchChange}
      variant="filled"
      fullWidth
      hiddenLabel
      className={classes.textField}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon className={classes.icon} />
          </InputAdornment>
        ),
        endAdornment: searchTerm && (
          <InputAdornment position="start">
            <CloseIcon
              className={`${classes.icon} ${classes.closeIcon}`}
              onClick={() => updateSearchTerm('')}
            />
          </InputAdornment>
        ),
      }}
    />
  );
};
